<template>
  <overlay-scrollbars :options="{ className: 'custom-scrollbar os-theme-dark', scrollbars: { visibility: 'auto', autoHide: 'leave', autoHideDelay: 400, dragScrolling: true, clickScrolling: false, touchSupport: true, snapHandle: false } }">
    <form @submit.prevent="handleSubmit">
      <div class="modal-window-header gray">
        <div class="modal-window-heading">
          <h3 class="icon-heading small">
            <span class="icon"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-object"></use></svg></span>
            <span v-if="!form.Objektnr">Nytt objekt</span>
            <span v-else>Nytt objekt #{{form.Objektnr}}</span>
          </h3>
        </div>

        <fieldset v-if="!creating && !deleting">
          <div class="grid-x grid-padding-x">
            <TheSelectInput v-model="form.Objekttype" containerClassName="cell medium-6" label="Kontraktstype" name="Objekttype" v-validate="'required'" required :items="objDropdown" :disabled="$store.getters.isFormLoading"/>
            <TheSelectInput v-model="form.Avdelingsid" containerClassName="cell medium-6" label="Avdeling" name="Avdelingsid" v-validate="'required'" required :items="avdDropdown" :disabled="true"/>

            <TheSelectInput v-model="form.Expertavdelingsid" containerClassName="cell medium-5" label="Ekspertavdeling" name="Expertavdelingsid" v-validate="'required'" required :items="expDropdown" :disabled="form.Avdelingsid === null || form.Avdelingsid === '' || expDropdown.length <= 1 || $store.getters.isFormLoading"/>
            <TheSelectInput v-model="form.Kategori" containerClassName="cell medium-5" label="Kategori" name="Kategori" v-validate="'required'" required :items="catDropdown" :disabled="form.Expertavdelingsid === null || form.Expertavdelingsid === '' || catDropdown.length <= 1 || $store.getters.isFormLoading"/>
            <TheSwitchInput v-model="rememberCat" :small="true" containerClassName="cell medium-2" label="Husk" labelTrue="Ja" labelFalse="Nei"/>

            <TheSelectInput v-model="form.Lokasjonid" containerClassName="cell medium-12" label="Lokasjon" name="Lokasjon" v-validate="'required'" required :items="lokDropdown" :disabled="$store.getters.isFormLoading"/>
            <TheAutoCompleteInput v-model.trim="form.Auksjon" ref="auksjon_input" containerClassName="cell medium-12" name="Auksjon" maxlength="10" v-validate="'required|max:10'" required :placeholder="'Velg auksjon'" :label="'Tilhørende auksjon'" :quicklinks="currQuicklinks" :onInputCallback="onAuksjonSearchInput" :onSelectCallback="onAuksjonSearchSelected" :onCreateCallback="openAuksjonModal" :onUpdateCallback="openAuksjonModal" :debounce="750" :minquery="2">
              <div slot="item" slot-scope="props">
                <strong>{{props.item.Auksjon}}</strong>, {{props.item.Beskrivelse}}
                <span v-if="props.item.Auksjon_dato">
                  ({{ props.item.Auksjon_dato | avantidate }})
                </span>
              </div>
              <li slot="quicklink" slot-scope="props"><a href="javascript:;" @click="onAuksjonSearchSelected({ Auksjon: props.quicklink.code })">{{props.quicklink.code}}</a></li>
            </TheAutoCompleteInput>
            <TheAutoCompleteInput v-show="!isVin" :disabled="kunstnerDisabled" v-model.trim="form.Kunstner" containerClassName="cell large-12" :placeholder="'Søk etter kunstner / designer…'" :label="'Kunstner / designer'" :onInputCallback="onKunstnerSearchInput" :onSelectCallback="onKunstnerSearchSelected" :onResetCallback="onKunstnerReset" :onCreateCallback="openKunstnerModal" :onUpdateCallback="openKunstnerModal" :debounce="750" :minquery="2">
              <div slot="item" slot-scope="props">
                <span v-if="props.item.val">
                  {{ props.item.val }}<span v-if="props.item.period">, {{ props.item.period }}</span>
                </span>
                <span v-else>
                  {{ props.item.key }}<span v-if="props.item.period">, {{ props.item.period }}</span>
                </span>
              </div>
            </TheAutoCompleteInput>
            <TheTextInput v-model="form.Tittel" containerClassName="cell medium-12" :label="titleLabel" name="Tittel" required v-validate="'required'"/>
            <TheTextArea containerClassName="cell medium-12" label="Beskrivelse/stilperiode" placeholder="Kort beskrivelse av objektet…" name="Objekt3" v-model="form.Objekt3" :rows="3" maxlength="100" v-validate="'max:100'"/>
          </div>
        </fieldset>
      </div>

      <div class="modal-window-body">
        <fieldset v-if="!creating && !deleting">
          <div class="grid-x grid-padding-x" id="error-fix-container">
            <TheIntegerInput v-model="form.Antall" containerClassName="cell medium-12 large-3 no-bottom-error" symbol="stk" label="Antall" name="Antall" v-validate="'required|integer'"/>
            <TheDecimalInput containerClassName="cell medium-12 large-3 no-bottom-error" label="Lav vurdering" name="Vurderingspris2" symbol="kr" v-model.trim="form.Vurderingspris2" maxlength="10" v-validate="'max:10|checkVurdering'"/>
            <TheDecimalInput containerClassName="cell medium-12 large-3 no-bottom-error" label="Høy vurdering" name="Vurderingspris1" symbol="kr" v-model.trim="form.Vurderingspris1" maxlength="10" v-validate="'max:10|checkVurdering'"/>
            <TheDecimalInput containerClassName="cell medium-12 large-3 no-bottom-error" label="Minstepris" name="Minstepris" symbol="kr" v-model.trim="form.Minstepris" maxlength="10" v-validate="{max: 10, checkMinstepris: form.Vurderingspris2 > 0 && form.Vurderingspris1 > 0, required: form.Vurderingspris2 > 0 && form.Vurderingspris1 > 0 }"/>
            <TheDecimalInput v-model="form.Salar" containerClassName="cell medium-6 large-6 no-bottom-error" symbol="%" label="Salær" name="Salar" maxlength="10" v-validate="'required|max:10|decimal'"/>
            <div class="cell medium-1">&nbsp;</div>
            <TheSwitchInput v-model="form.Lagerleieinfo" containerClassName="cell medium-4" label="Lagerleie" labelTrue="Ja" labelFalse="Nei" :trueValue="1" :falseValue="0"/>
          </div>
        </fieldset>
        <fieldset v-else-if="creating">
          <div class="grid-x grid-padding-x">
            <div class="cell medium-12 large-12">
              Oppretter objekt, vennligst vent...
              <br><br>
            </div>
          </div>
        </fieldset>
        <fieldset v-else-if="deleting">
          <div class="grid-x grid-padding-x">
            <div class="cell medium-12 large-12">
              Sletter objekt, vennligst vent...
              <br><br>
            </div>
          </div>
        </fieldset>
      </div>

      <div class="modal-window-footer">
        <div class="grid-x">
          <div class="cell align-right">
            <a href="javascript:;" @click="closeButtonClick()" class="button primary transparent large modal-close show-for-medium">Avbryt</a>

            <a v-if="$store.getters.isFormSubmittable" href="javascript:;" @click="submitButtonClickAndRedirect()" class="button primary submit large">Gå til objekt</a>
            <a v-if="$store.getters.isFormProcessing && buttonPressed === 'save-redirect'" href="javascript:;" class="button primary submit large loading">Gå til objekt...<span class="loader-spinner"></span></a>
            <a v-if="$store.getters.isFormLoading || ($store.getters.isFormProcessing && buttonPressed !== 'save-redirect')" href="javascript:;" class="button primary submit large" disabled>Gå til...</a>

            <a v-if="$store.getters.isFormSubmittable" href="javascript:;" @click="submitButtonClickAndPrint()" class="button primary submit large">Lagre & skriv ut etikett</a>
            <a v-if="$store.getters.isFormProcessing && buttonPressed === 'save-print'" href="javascript:;" class="button primary submit large loading">Lagre & skriv ut etikett...<span class="loader-spinner"></span></a>
            <a v-if="$store.getters.isFormLoading || ($store.getters.isFormProcessing && buttonPressed !== 'save-print')" href="javascript:;" class="button primary submit large" disabled>Lagre & skriv ut...</a>

            <a v-if="$store.getters.isFormSubmittable" href="javascript:;" @click="submitButtonClick()" class="button primary submit large">Lagre</a>
            <a v-if="$store.getters.isFormProcessing && buttonPressed === 'save'" href="javascript:;" class="button primary submit large loading">Lagrer...<span class="loader-spinner"></span></a>
            <a v-if="$store.getters.isFormLoading || ($store.getters.isFormProcessing && buttonPressed !== 'save')" href="javascript:;" class="button primary submit large" disabled>Lagre</a>
          </div>
        </div>
      </div>

      <a href="javascript:;" class="modal-close modal-window-close-button" @click="xButtonClick()">
        <span>Lukk</span>
        <svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-close"></use></svg>
      </a>
    </form>
  </overlay-scrollbars>
</template>

<script>
import ObjektService from '@/services/ObjektService'
import KundeService from '@/services/KundeService'
import AuksjonService from '@/services/AuksjonService'
import KunstnerService from '@/services/KunstnerService'
import TheAutoCompleteInput from '../Inputs/TheAutoCompleteInput'
import bus from '@/plugins/event-bus'

export default {
  name: 'KundeKontraktObjektCreateModal',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    TheAutoCompleteInput
  },
  props: {
    kunde: {
      type: Object,
      required: true
    },
    kontrakt: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      /* eslint-disable-next-line */
      rememberCat: false, // localStorage.new_obj_kategori ? true : false,
      form: {},
      original: {},
      lokDropdown: [],
      objDropdown: [], // Objekt type drowdown
      avdDropdown: [], // Avdeling dropdown
      expList: [], // Expertavdeling
      catList: [], // Categories
      lblList: [], // Labels
      quicklinks: [],
      savedAuctions: {},
      creating: true,
      buttonPressed: null,
      deleting: false
    }
  },
  created () {
    bus.$off('auksjon-saved')
    bus.$off('kunstner-saved')
    bus.$off('new-object-delete')

    // Set form not submittable
    this.setFormLoading(true)

    // Custom rules
    this.$validator.extend('checkVurdering', {
      validate: this.checkVurdering,
      getMessage: (field, params, data) => data ? data.message : 'Feil vurdering'
    })

    this.$validator.extend('checkMinstepris', {
      validate: this.checkMinstepris,
      getMessage: (field, params, data) => data ? data.message : 'Feil minstepris'
    })

    // Load quicklinks if have any...
    this.loadQuickLinks()

    // Make the form object
    this.makeFormItem()
  },
  mounted () {
    window.jQuery('.modal-window-body').attr('tabindex', -1).css({outline: 'none'}).focus()

    bus.$on('auksjon-saved', (data) => this.onAuksjonSaved(data))
    bus.$on('kunstner-saved', (data) => this.onKunstnerSaved(data))
    bus.$on('new-object-delete', () => this.onModalCloseEvent())
  },
  watch: {
    'form': {
      handler (newVal, oldVal) {
        // var self = this
        // var diff = this._.omitBy(newVal, function(v, k) {
        //   return self.original[k] === v
        // })
        if (this.isFormSubmittable() && !this._.isEqual(newVal, this.original)) {
          this.setFormDirty('newobjektform', true)
        } else {
          this.setFormDirty('newobjektform', false)
        }
      },
      deep: true
    },
    'form.Avdelingsid': function (newVal, oldVal) {
      if (oldVal !== undefined && parseInt(newVal) !== parseInt(oldVal)) {
        this.$set(this.form, 'Expertavdelingsid', null)
        this.$set(this.form, 'Kategori', null) // localStorage.new_obj_kategori ? localStorage.new_obj_kategori : null)

        // If there is an auksjon string
        if (this.form.Auksjon) {
          this.$refs.auksjon_input.reload()
        }
      }

      // Vin - Reset kunstner
      if (newVal !== undefined && parseInt(newVal) === 2) {
        this.onKunstnerSearchSelected(null)
      }

      // If only have one Expertavdeling option (+ the disabled "Velg option")
      if (this.expDropdown.length === 2) {
        this.$set(this.form, 'Expertavdelingsid', this.expDropdown[this.expDropdown.length - 1].key)
      }
    },
    'form.Expertavdelingsid': function (newVal, oldVal) {
      if (oldVal !== undefined && parseInt(newVal) !== parseInt(oldVal)) {
        this.$set(this.form, 'Kategori', null)
      }

      // If only have one Hovedkategori option (+ the disabled "Velg option")
      if (this.catDropdown.length === 2) {
        this.$set(this.form, 'Kategori', this.catDropdown[this.catDropdown.length - 1].key)
      } else if (!newVal) {
        this.$set(this.form, 'Kategori', null)
      } else {
        const vm = this
        const stored = this.loadLastSaved() || {}

        this.$set(this.form, 'Kategori', null)

        if (oldVal === undefined && stored.Kategori) {
          setTimeout(function () {
            vm.$set(vm.form, 'Kategori', stored.Kategori)
          })
        }
      }
    }
  },
  computed: {
    isVin: function () {
      return parseInt(this.form.Avdelingsid || 0) === 2
    },
    titleLabel: function () {
      let expertavd = parseInt(this.form.Expertavdelingsid || 0)

      // Kunst
      if (expertavd === 2 || expertavd === 5) {
        return 'Tittel'
      } else {
        return 'Tittel/Objekt'
      }
    },
    kunstnerDisabled: function () {
      return this.form.Avdelingsid === '' || this.form.Avdelingsid === null || parseInt(this.form.Avdelingsid) === 2
    },
    expDropdown: function () {
      let retval = []

      if (this.$store.getters.isFormLoading) {
        retval.unshift({key: this.form.Expertavdelingsid, val: 'Laster...'})
      } else if (this.form.Avdelingsid === null || this.form.Avdelingsid === '') {
        retval.unshift({key: null, val: 'Velg avdeling først'})
      } else {
        retval = this.expList.filter((i) => {
          return parseInt(i.Avdelingsid) === parseInt(this.form.Avdelingsid)
        })

        if (retval.length < 1) {
          retval.unshift({key: null, val: 'Ingen ekspertavdeling'})
        } else {
          retval.unshift({key: null, val: 'Velg ekspertavdeling', disabled: true})
        }
      }

      return retval
    },
    catDropdown: function () {
      let retval = []

      if (this.$store.getters.isFormLoading) {
        retval.unshift({key: this.form.Kategori, val: 'Laster...'})
      } else if (this.form.Expertavdelingsid === null || this.form.Expertavdelingsid === '') {
        retval.unshift({key: null, val: 'Velg ekspertavdeling først'})
      } else {
        let eidIntVal = parseInt(this.form.Expertavdelingsid)

        retval = this.catList.filter((i) => {
          return i.expertavdelingsid.includes(eidIntVal)
        })

        if (retval.length < 1) {
          retval.unshift({key: null, val: 'Ingen Kategori'})
        } else {
          retval.unshift({key: null, val: 'Velg Kategori', disabled: true})
        }
      }

      return retval
    },
    subCatDropdown: function () {
      let retval = []

      if (this.form.Kategori === null || this.form.Kategori === '') {
        retval.unshift({key: null, val: 'Velg hovedkategori først'})
      } else {
        let flat = this._.has(this.lblList, 'flat') && this.lblList.flat.length > 0 ? this.lblList.flat : []

        retval = flat.filter((i) => {
          return !i.parent && i.kategori === this.form.Kategori
        })

        if (retval.length < 1) {
          retval.unshift({key: null, val: 'Ingen underkategori'})
        } else {
          retval.unshift({key: null, val: 'Velg underkategori', disabled: true})
        }
      }

      return retval
    },
    currQuicklinks: function () {
      const aid = String(this.form.Avdelingsid)

      if (this.quicklinks.hasOwnProperty(aid)) {
        return this.quicklinks[aid]
      }

      return []
    }
  },
  methods: {
    onModalCloseEvent: function () {
      // Don't allow close from overlay...
      // This event is assigned from KundeKontraktObjektList - where open modal is done
    },
    loadQuickLinks: function () {
      this.quicklinks = localStorage.lastSavedQuicklinks ? JSON.parse(localStorage.lastSavedQuicklinks) : {}
    },
    saveQuickLinks: function (auction) {
      const aid = String(this.form.Avdelingsid)

      const links = this.quicklinks.hasOwnProperty(aid) ? this._.cloneDeep(this.quicklinks[aid]) : []
      const found = links.find(a => a.code === auction.code)

      if (!found) {
        if (links.length >= 5) {
          links.pop()
        }

        links.unshift(auction)

        this.$set(this.quicklinks, aid, links)
        localStorage.lastSavedQuicklinks = JSON.stringify(this.quicklinks)
      }
    },
    loadLastAuction: function () {
      try {
        this.savedAuctions = localStorage.lastSavedAuksjon ? JSON.parse(localStorage.lastSavedAuksjon) : {}

        const aid = String(this.form.Avdelingsid)

        if (this.savedAuctions.hasOwnProperty(aid)) {
          this.form.Auksjon = this.savedAuctions[aid]

          if (!this.quicklinks[aid]) {
            this.saveQuickLinks({ code: this.form.Auksjon })
          }
        }
      } catch (e) {
        this.savedAuctions = {}
      }
    },
    saveLastAuction: function () {
      this.savedAuctions[String(this.form.Avdelingsid)] = this.form.Auksjon

      localStorage.lastSavedAuksjon = JSON.stringify(this.savedAuctions)
    },
    onKunstnerSearchInput: function (query) {
      return new Promise(resolve => {
        KunstnerService.search(query, 1)
          .then((response) => {
            const found = response.data.map(x => {
              return { key: x.Idnr, val: x.name_original, period: x.period_dates || '' }
            })

            resolve({ data: found })
          })
      })

      // Old
      // return KunstnerService.search(query)
    },
    onKunstnerSearchSelected: function (item) {
      if (!item) {
        this.form.Kunstnerid = null
      } else {
        this.form.Kunstner = item.val || ''
        this.form.Kunstnerid = item.key || null
      }
    },
    onKunstnerReset: function () {
      this.form.Kunstner = null
      this.form.Kunstnerid = null
    },
    onKunstnerSaved: function (item) {
      this.form.Kunstner = item.Navn || ''
      this.form.Kunstnerid = item.Idnr || null
    },
    openKunstnerModal: function (item) {
      if (item) {
        if (this.form.Kunstnerid) {
          this.openModal('kunstner-form-modal', { kunstnerIdentifier: this.form.Kunstnerid })
        } else {
          this.$helpers.showErrorAlert('Feil', 'Du kann ikke rediger en kunstner som ikke er lagret i databasen')
        }
      } else {
        this.openModal('kunstner-form-modal')
      }
    },
    onAuksjonSearchInput: function (query) {
      return AuksjonService.searchOnObjektCreate(query, this.form.Avdelingsid)
    },
    onAuksjonSearchSelected: function (item) {
      if (item) {
        this.form.Auksjon = item.Auksjon || ''

        if (this.form.Auksjon) {
          this.saveQuickLinks({ code: item.Auksjon })
        }
      }
    },
    onAuksjonSaved: function (item) {
      this.form.Auksjon = item.Auksjon || ''

      if (this.form.Auksjon) {
        this.saveQuickLinks({ code: item.Auksjon })
      }
    },
    openAuksjonModal: function (item) {
      if (item) {
        this.openModal('auksjon-form-modal', {auksjonIdentifier: item})
      } else {
        this.openModal('auksjon-form-modal')
      }
    },
    loadLastSaved: function () {
      try {
        const retval = JSON.parse(localStorage.getItem('last_saved_new_obj') || {})
        const key = '' + this.kontrakt.Avdelingsid

        if (!retval.hasOwnProperty(key)) {
          return null
        }

        return retval[key]
      } catch (e) {
        return null
      }
    },
    saveLastSaved: function (data) {
      let stored

      try {
        stored = localStorage.getItem('last_saved_new_obj')
          ? JSON.parse(localStorage.getItem('last_saved_new_obj'))
          : {}
      } catch (e) {
        stored = {}
      }

      try {
        const key = '' + this.kontrakt.Avdelingsid

        stored[key] = data

        localStorage.setItem('last_saved_new_obj', JSON.stringify(stored))
      } catch (e) {
        // Ignore
        console.log('Save to local storage error:')
        console.log(e)
      }
    },
    makeFormItem: function () {
      // Load last stored values for this avdeling
      const stored = this.loadLastSaved() || {}

      this.rememberCat = stored.hasOwnProperty('Kategori')

      let lokId = localStorage.getItem('new_obj_lokasjonid')
      if (!lokId || lokId === 'null') {
        lokId = '159' // Utlevert
      }

      let defaults = {
        Avdelingsid: this.kontrakt.Avdelingsid,
        Expertavdelingsid: stored.Expertavdelingsid || null,
        Kategori: stored.Kategori || null,
        Objekttype: this.kontrakt.Kontrakttype,
        Lokasjonid: lokId,
        Auksjon: null,
        Salar: this.kontrakt.Salar,
        Forsikring: this.kontrakt.Forsikring,
        Antall: 1,
        Vurderingspris1: null,
        Vurderingspris2: null,
        Minstepris: null,
        Oppgjorgodkjendt: 'T',
        Lagerleieinfo: 1,
        spesifikasjoner: true
      }

      // Fotokost calc based on avdeling
      let choosenAvdelingsId = parseInt(defaults.Avdelingsid)
      if (choosenAvdelingsId === 1) {
        defaults.Fotokostnad = 250
      } else if (choosenAvdelingsId === 2) {
        defaults.Fotokostnad = 0
      } else {
        defaults.Fotokostnad = 1000
      }

      // Load last fraktgruppe id
      if (choosenAvdelingsId === 2) {
        const Fraktgruppeid = localStorage.getItem('last-invoice-group-wine')
        if (Fraktgruppeid) {
          defaults.Fraktgruppeid = Fraktgruppeid
        }
      }

      KundeService.saveKundeKontraktObjekt(this.kunde.Kundenr, this.kontrakt.Kontraktnr, null, defaults)
        .then((response) => {
          // Take care of the response - except Kategori
          // response.Kategori = defaults.Kategori || response.data.Kategori
          this.form = Object.assign({}, this.form, response.data)

          this.$set(this.form, 'Avdelingsid', response.data.Avdelingsid)
          this.$set(this.form, 'Expertavdelingsid', response.data.kategori.Expertavdelingsid)
          this.$set(this.form, 'Kategori', response.data.Kategori)

          // Emit event so we can update list
          bus.$emit('kunde-kontrakt-objekt-created', this.form)
        })
        .catch((error) => {
          // Put API errors into the error container
          this.$helpers.sendApiErrorsToValidator(error, this.errors)

          // Check if new errors appeared
          if (this.errors.any()) {
            this.$validator.resume()
            this.$helpers.showErrorAlert('Kunne ikke lagre objekt', 'Vennligst prøv igjen')
          } else {
            this.$helpers.showErrorAlert('Kunne ikke lagre objekt', 'Vennligst prøv igjen', 2500)
          }
          this.form = Object.assign({}, defaults, this.form)
        })
        .then(() => {
          // If have quicklinks, take first and assign to auksjon.
          // if (this.quicklinks && this.quicklinks.length > 0) {
          //   this.form.Auksjon = this.quicklinks[0].code
          // }

          this.loadLastAuction()

          // Make copy of form as original - will be used to check if form has changed
          this.original = this._.cloneDeep(this.form)

          this.creating = false

          // Load data needed in the form
          this.fetch()
        })

      // this.form = Object.assign({}, this.form, defaults)
    },
    fetch: function () {
      this.objDropdown.unshift({key: this.form.Objekttype, val: 'Laster...'})
      this.avdDropdown.unshift({key: this.form.Avdelingsid, val: 'Laster...'})
      this.lokDropdown.unshift({key: this.form.Lokasjonid, val: 'Laster...'})

      const skipCategories = ['US', 'ZS']

      ObjektService.loadFormDropdowns()
        .then((response) => {
          this.avdDropdown = response.avdeling
          this.avdDropdown.unshift({key: null, val: 'Velg avdeling', disabled: true})

          if (this.form.Avdelingsid === 3) {
            this.objDropdown = response.objekttype.filter(type => type.key === 'K')
          } else {
            this.objDropdown = response.objekttype
          }
          this.objDropdown.unshift({key: null, val: 'Velg kontraktstype', disabled: true})

          this.lokDropdown = response.lokasjon
          this.lokDropdown.unshift({key: null, val: 'Velg lokasjon', disabled: true})

          this.expList = response.expertavdeling
          this.catList = response.kategori.filter(c => !skipCategories.includes(c.key))
          this.lblList = response.label
        })
        .catch(() => {
          // Skip error
          this.$helpers.showErrorAlert('Feil', 'Kunne ikke laste ned innstillinger')
        })
        .then(() => {
          this.setFormLoading(false)
        })
    },
    xButtonClick: function () {
      if (this.isFormDirty('newobjektform')) {
        this.$helpers.showErrorAlert('', 'Du har gjort endringer på objektet,<br>vennligst lagre før du går videre.', true)
      } else if (!this.$store.getters.isFormProcessing) {
        this.handleDelete()
      }
    },
    closeButtonClick: function () {
      if (!this.$store.getters.isFormProcessing && this.form.Objektid) {
        this.handleDelete()
      }
    },
    handleDelete: function () {
      this.deleting = true

      this.setFormProcessing(true)

      KundeService.deleteKundeKontraktObjekt(this.kunde.Kundenr, this.kontrakt.Kontraktnr, this.form.Objektid)
        .then((response) => {
          bus.$emit('kunde-kontrakt-objekt-deleted')
        })
        .catch(() => {
          console.log('Unable to delete')
        })
        .then(() => {
          this.deleting = false
          this.setFormProcessing(false)
          this.setFormLoading(false)
          this.$emit('close')
        })
    },
    submitButtonClick: function () {
      this.buttonPressed = 'save'
      this.handleSubmit()
    },
    submitButtonClickAndPrint: function () {
      this.buttonPressed = 'save-print'
      this.handleSubmit()
    },
    submitButtonClickAndRedirect: function () {
      this.buttonPressed = 'save-redirect'

      this.closeModal()
      sessionStorage.openEdit = 1 // Go to edit mode
      this.loadObjektModal(this.form.Objektid)
      // this.handleSubmit()
    },
    handleSubmit: function () {
      if (!this.isFormSubmittable()) {
        this.buttonPressed = null
        return
      }

      // Fotokost calc based on avdeling
      let choosenAvdelingsId = parseInt(this.form.Avdelingsid)

      if (choosenAvdelingsId === 1) {
        this.form.Fotokostnad = 250
      } else if (choosenAvdelingsId === 2) {
        this.form.Fotokostnad = 0
      } else {
        this.form.Fotokostnad = 1000
      }

      this.setFormProcessing(true)

      this.$validator.validateAll()
        .then((result) => {
          if (!result) {
            // Validation Failed
            this.setFormProcessing(false)
            this.$helpers.showErrorAlert('Kunne ikke lagre endringer', 'Vennligst korriger feil i skjemaet')
          } else {
            // At the moment title is saved in the Katalogtekst1 field.
            this.form.Katalogtekst1 = this.form.Tittel

            KundeService.saveKundeKontraktObjekt(this.kunde.Kundenr, this.kontrakt.Kontraktnr, this.form.Objektid, this.form)
              .then((response) => {
                // Take care of the response
                let newObjekt = response.data

                localStorage.new_obj_lokasjonid = this.form.Lokasjonid
                // localStorage.new_obj_objekttype = this.form.Objekttype

                // localStorage.new_obj_avdeling = this.form.Avdelingsid
                // localStorage.new_obj_expavdeling = this.form.Expertavdelingsid

                if (this.rememberCat) {
                  // localStorage.new_obj_kategori = this.form.Kategori

                  this.saveLastSaved({Expertavdelingsid: this.form.Expertavdelingsid, Kategori: this.form.Kategori})
                } else if (localStorage.new_obj_kategori) {
                  // localStorage.removeItem('new_obj_kategori')

                  this.saveLastSaved({Expertavdelingsid: this.form.Expertavdelingsid})
                }

                this.saveLastAuction()

                // Show success alert
                this.$helpers.showSuccessAlert('Objekt er lagret')

                // Emit event so we can update list
                bus.$emit('kunde-kontrakt-objekt-created', newObjekt)

                if (this.buttonPressed === 'save-print') {
                  this.closeModal()
                  this.openModal(
                    'print-labels-modal',
                    {
                      objekt: newObjekt
                    },
                    'medium'
                  )
                } else if (this.buttonPressed === 'save-redirect') {
                  this.closeModal()

                  sessionStorage.openEdit = 1 // Go to edit mode
                  this.loadObjektModal(newObjekt.Objektid)
                  // bus.$emit('show-kunde-kontrakt-objekt', this.kontrakt, newObjekt, '#edit')
                } else {
                  // And close modal
                  this.closeModal()
                }
              })
              .catch((error) => {
                // Put API errors into the error container
                this.$helpers.sendApiErrorsToValidator(error, this.errors)

                // Check if new errors appeared
                if (this.errors.any()) {
                  this.$validator.resume()
                  this.$helpers.showErrorAlert('Kunne ikke lagre endringer', 'Vennligst korriger feil i skjemaet')
                } else {
                  this.$helpers.showErrorAlert('Kunne ikke lagre endringer', 'Vennligst prøv igjen', 2500)
                }
              })
              .then(() => {
                this.setFormProcessing(false)
                this.setFormDirty(false)
                this.buttonPressed = null
              })
          }
        })
    },
    checkVurdering: function (value) {
      return new Promise(resolve => {
        let low = parseInt(this.form.Vurderingspris2)
        let high = parseInt(this.form.Vurderingspris1)

        if (low > 0 && high > 0) {
          if (low >= high) {
            resolve({ valid: false, data: { message: 'Høy vurdering er lavere eller lik lav vurdering' } })
            return
          }
        }

        resolve({ valid: true })
      })
    },
    checkMinstepris: function (value) {
      return new Promise(resolve => {
        let low = parseInt(this.form.Vurderingspris2)
        let val = value !== '' && value !== null ? parseInt(value) : null

        if (!low) {
          resolve({ valid: true })
        } else if (val === null) {
          resolve({ valid: false, data: { message: 'Minstepris er obligatorisk' } })
        } else if (val > low) {
          resolve({ valid: false, data: { message: 'Minstepris er høyere enn lav vurdering' } })
        } else {
          resolve({ valid: true })
        }
      })
    }
  }
}
</script>
<style>
  /** Temporary fix */
  #error-fix-container .form-error-label {
    bottom: 0;
    top: calc(100% + 0.2rem)
  }
</style>



// WEBPACK FOOTER //
// src/components/Admin/KundeKontraktObjektCreateModal.vue